import React from 'react';
import { useTheme } from '@mui/material/styles';

/**
 * @param {number} page           current zero-based page index
 * @param {number} count          total number of items
 * @param {number} rowsPerPage    number of items per page
 * @param {(newPage: number) => void} onPageChange
 * @param {number} siblingCount   pages to show on each side of current
 * @param {number} boundaryCount  pages to show at beginning and end
 */
const Pagination = ({
    page = 0,
    count = 0,
    rowsPerPage = 10,
    onPageChange = () => { },
    siblingCount = 1,
    boundaryCount = 2,
}) => {
    const theme = useTheme();
    const totalPages = Math.ceil(count / rowsPerPage);

    // Build a windowed pagination list with ellipses
    const getPaginationRange = () => {
        const totalPageNumbers = boundaryCount * 2 + siblingCount * 2 + 3;
        // 3 = first, last, and current
        if (totalPages <= totalPageNumbers) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const leftSiblingIndex = Math.max(page + 1 - siblingCount, boundaryCount + 2);
        const rightSiblingIndex = Math.min(
            page + 1 + siblingCount,
            totalPages - boundaryCount - 1
        );

        const range = [];

        // start boundary
        for (let i = 1; i <= boundaryCount; i++) {
            range.push(i);
        }

        // left ellipsis
        if (leftSiblingIndex > boundaryCount + 2) {
            range.push('left-ellipsis');
        } else if (leftSiblingIndex === boundaryCount + 2) {
            range.push(boundaryCount + 1);
        }

        // middle pages
        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
            range.push(i);
        }

        // right ellipsis
        if (rightSiblingIndex < totalPages - boundaryCount - 1) {
            range.push('right-ellipsis');
        } else if (rightSiblingIndex === totalPages - boundaryCount - 1) {
            range.push(totalPages - boundaryCount);
        }

        // end boundary
        for (let i = totalPages - boundaryCount + 1; i <= totalPages; i++) {
            range.push(i);
        }

        return range;
    };

    const paginationRange = getPaginationRange();

    const arrowStyle = {
        width: 24,
        height: 24,
        stroke: theme.palette.text.secondary,
        opacity: 1,
    };

    return (
        <div className="flex justify-end items-center mt-8 gap-1">
            {/* Previous */}
            <button
                onClick={() => onPageChange(Math.max(page - 1, 0))}
                disabled={page === 0}
                className="p-2 rounded-full"
                style={{ color: theme.palette.text.secondary }}
            >
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                    style={{ ...arrowStyle, opacity: page === 0 ? 0.5 : 1 }}
                >
                    <path d="m15 18-6-6 6-6" />
                </svg>
            </button>

            {/* Page Numbers */}
            {paginationRange.map((item, idx) => {
                if (item === 'left-ellipsis' || item === 'right-ellipsis') {
                    return (
                        <span key={item + idx} className="w-8 h-8 flex items-center justify-center">
                            …
                        </span>
                    );
                }

                const isActive = item === page + 1;
                return (
                    <button
                        key={item}
                        onClick={() => onPageChange(item - 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer text-sm"
                        style={{
                            backgroundColor: isActive
                                ? theme.palette.primary.main
                                : 'transparent',
                            color: isActive
                                ? theme.palette.common.white
                                : theme.palette.text.primary,
                        }}
                    >
                        {item}
                    </button>
                );
            })}

            {/* Next */}
            <button
                onClick={() => onPageChange(Math.min(page + 1, totalPages - 1))}
                disabled={page >= totalPages - 1}
                className="p-2 rounded-full"
                style={{ color: theme.palette.text.secondary }}
            >
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                    style={{ ...arrowStyle, opacity: page >= totalPages - 1 ? 0.5 : 1 }}
                >
                    <path d="m9 18 6-6-6-6" />
                </svg>
            </button>
        </div>
    );
};

export default Pagination;
